import React from "react";
import styled from "styled-components";

import { MerchProductFront } from "@services/merch/types/api/merch-product-front";
import useIsMobile from "../../hooks/is-mobile";
import { Flex } from "../flex";

export default function ProductInfo({
  className,
  product,
  inDetail,
  inCart,
  size,
  color,
  noPrice,
  isBig,
  isHighlight,
}: {
  className?: string;
  product: MerchProductFront;
  inDetail?: boolean;
  inCart?: boolean;
  size?: string;
  color?: string;
  noPrice?: boolean;
  isBig?: boolean;
  isHighlight?: boolean;
}) {
  const isMobile = useIsMobile();
  const Content = () => (
    <>
      <TitleWrapper>
        {inDetail && !noPrice && isMobile && (
          <Price className="price">
            ${product.price.toFixed(2).toLocaleString()}
          </Price>
        )}
        <Title className="title">{product.title}</Title>
        {inCart && size ? (
          <Details>
            Size: {size}
            {color && `, ${color}`}
          </Details>
        ) : null}
      </TitleWrapper>
      {!noPrice && !isMobile && (
        <Price className={`price ${isBig ? "big" : ""}`}>
          ${product.price.toFixed(2).toLocaleString()}
        </Price>
      )}
      {inDetail && product.description ? (
        <Description>{product.description}</Description>
      ) : null}
    </>
  );

  return (
    <InformationWrapper
      className={`${className || ""} ${inDetail ? "in-detail" : ""} ${
        inCart ? "in-cart" : ""
      } ${isHighlight ? "is-highlight" : ""}`}
    >
      <Content />
    </InformationWrapper>
  );
}

const InformationWrapper = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;

  &.in-detail {
    @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
    }
    .title,
    .price {
      font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
      text-align: left;
    }
    .title {
      font-size: 24px;
      font-weight: 600;
      line-height: 100%; /* 24px */
      letter-spacing: -1px;
      margin-inline-end: 80px;
      margin-block-end: 20px;

      overflow: visible;
      display: block;
      text-overflow: unset;
      white-space: unset;
      word-break: unset;

      @media (max-width: ${({ theme }) => `${theme.breakpoints.laptopS}px`}) {
        margin-inline-end: 0;
      }
    }
    .price {
      color: ${({ theme }) => theme.colors.textGrey};
      margin: 0;
      margin-block-end: 24px;
      font-size: 14px;
      font-weight: 600;
      line-height: normal;
      text-transform: uppercase;
    }
  }

  &.in-cart {
    width: 100%;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    .title {
      color: ${({ theme }) => theme.colors.background};
      text-align: left;
    }
  }

  &.is-highlight {
    .title {
      font-family: inherit;
      text-align: left;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px; /* 150% */
    }
    .price {
      margin-block-start: 2px;
      margin-inline: 0;
      text-align: left;
      color: ${({ theme }) => theme.colors.textGrey};
    }
  }
`;

const TitleWrapper = styled.div`
  max-width: 100%;
`;

const Title = styled.h4`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;

  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2; /* start showing ellipsis when 3rd line is reached */
  white-space: pre-wrap; /* let the text wrap preserving spaces */
`;

const Price = styled.h6`
  color: ${({ theme }) => theme.colors.primary};
  text-align: center;
  leading-trim: both;
  text-edge: cap;
  font-family: ${({ theme }) => theme.fonts.UrbaneDemiBold};
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  margin: 4px auto 0;

  &.big {
    margin-top: 16px;
  }
`;

const Details = styled.h6`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textGrey};
  opacity: 0.6;
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 145%; /* 20.3px */
`;
